import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import axios from 'axios';
import {
  Trophy, Eye, FileText, Paperclip, CaretRight, CaretDown, ChartBar, CheckCircle, Hourglass,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import EntrepriseReponseDetailModal from './EntrepriseReponseDetailModal';
import DCEComparatifDialog from './DCEComparatifDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_BADGE = {
  non_attribue: { label: 'Non attribué', cls: 'bg-[#F5F5F3] text-[#6B6B6B]' },
  en_consultation: { label: 'En consultation', cls: 'bg-[#E0F2FE] text-[#0369A1]' },
  attribue: { label: 'Attribué', cls: 'bg-[#E8F5E9] text-[#2D6A4F]' },
  en_cours: { label: 'En cours', cls: 'bg-[#FFFBEB] text-[#D4880F]' },
  receptionne: { label: 'Réceptionné', cls: 'bg-[#F0FDF4] text-[#2D6A4F]' },
  contentieux: { label: 'Contentieux', cls: 'bg-[#FEF2F2] text-[#C53030]' },
};

function fmtEur(n) {
  if (n === null || n === undefined) return '—';
  return `${Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return '—'; }
}

import DpgfDceConsistencyBanner from './DpgfDceConsistencyBanner';

// RP02 — `canWrite` (calculé par ProjectMarches sur les droits serveur) masque
// « Attribuer » (PUT /marches/{lot} : rôles projet MOE seulement).
export default function MarchesHub({ chantierId, onAttribuer, onChanged, canWrite = true }) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [detailEnt, setDetailEnt] = useState(null);
  const [dceId, setDceId] = useState(null);
  const [dceStatut, setDceStatut] = useState(null);
  // CSL-04 — comparatif ouvert depuis le hub : { dceId, lotId (id du lot dans le snapshot DCE) }
  const [comparatifCible, setComparatifCible] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [marchesRes, dceRes] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/marches/with-propositions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/dce`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setLots(marchesRes.data || []);
      if (dceRes.data?.dce?.id) {
        setDceId(dceRes.data.dce.id);
        setDceStatut(dceRes.data.dce.statut || null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, [chantierId]);

  useEffect(() => { reload(); }, [reload]);

  const handleAttribuer = async (lotId, ent) => {
    if (!window.confirm(`Attribuer le lot à ${ent.entreprise_nom} pour ${fmtEur(ent.montant_ht)} HT ?`)) return;
    try {
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/marches/${lotId}`,
        {
          entreprise_id: ent.entreprise_id,
          entreprise_nom: ent.entreprise_nom,
          montant_marche: ent.montant_ht,
          statut: 'attribue',
        },
        { withCredentials: true }
      );
      toast.success(`Lot attribué à ${ent.entreprise_nom}`);
      reload();
      onChanged?.();
      onAttribuer?.(lotId, ent.entreprise_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur attribution');
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-[#9A9A9A]">Chargement du hub Marchés…</div>;
  }

  const totalLots = lots.length;
  // A5 / P4#3 — un lot en cours ou réceptionné reste attribué (même total que Finances)
  const lotsAttribues = lots.filter(l => ['attribue', 'en_cours', 'receptionne', 'contentieux'].includes(l.statut)).length;
  const lotsAvecOffres = lots.filter(l => l.nb_reponses > 0).length;
  const totalEnvelope = lots.reduce((s, l) => s + (l.montant_marche || 0), 0);

  return (
    <div className="space-y-4" data-testid="marches-hub">
      {/* Cohérence DPGF↔DCE v31 */}
      <DpgfDceConsistencyBanner chantierId={chantierId} onActionComplete={reload} />
      {/* Header KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-[#E0E0DB] p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Lots</p>
          <p className="text-2xl font-bold text-[#1E3A5F]">{totalLots}</p>
        </div>
        <div className="border border-[#E0E0DB] p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] flex items-center gap-1">
            <ChartBar size={10} /> Avec offres
          </p>
          <p className="text-2xl font-bold text-[#1E3A5F]" data-testid="hub-kpi-avec-offres">{lotsAvecOffres}</p>
        </div>
        <div className="border border-[#E0E0DB] p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] flex items-center gap-1">
            <CheckCircle size={10} /> Attribués
          </p>
          <p className="text-2xl font-bold text-[#2D6A4F]" data-testid="hub-kpi-attribues">{lotsAttribues}</p>
        </div>
        <div className="border border-[#E0E0DB] p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Enveloppe attribuée</p>
          <p className="text-2xl font-bold text-[#1E3A5F]">{fmtEur(totalEnvelope)}</p>
        </div>
      </div>

      {totalLots === 0 ? (
        <div className="border-2 border-dashed border-[#E0E0DB] p-10 text-center">
          <p className="text-sm text-[#6B6B6B]">Aucun lot. Ajoutez des lots ci-dessous pour commencer.</p>
        </div>
      ) : (
        <div className="border border-[#E0E0DB] overflow-x-auto bg-white">
          <table className="w-full text-sm" data-testid="marches-hub-table">
            <thead className="bg-[#1E3A5F] text-white text-xs uppercase tracking-wider">
              <tr>
                <th className="px-2 py-2 w-8"></th>
                <th className="px-3 py-2 text-left">Lot</th>
                <th className="px-3 py-2 text-center">Offres</th>
                <th className="px-3 py-2 text-right">Fourchette HT</th>
                <th className="px-3 py-2 text-left">Attribué à</th>
                <th className="px-3 py-2 text-right">Montant marché</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2 text-right w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lots.map(lot => {
                const isOpen = expandedId === lot.id;
                const cfg = STATUT_BADGE[lot.statut] || STATUT_BADGE.non_attribue;
                return (
                  <React.Fragment key={lot.id}>
                    <tr
                      className={`border-t border-[#E0E0DB] hover:bg-[#FAFAF8] ${isOpen ? 'bg-[#FAFAF8]' : ''}`}
                      data-testid={`hub-lot-row-${lot.id}`}
                    >
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={() => setExpandedId(isOpen ? null : lot.id)}
                          className="text-[#6B6B6B] hover:text-[#1E3A5F]"
                          data-testid={`hub-lot-toggle-${lot.id}`}
                          disabled={lot.nb_reponses === 0}
                          title={lot.nb_reponses === 0 ? 'Aucune offre' : (isOpen ? 'Replier' : 'Voir les offres')}
                        >
                          {lot.nb_reponses === 0 ? <span className="text-[#D0D0D0]">—</span> : (isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />)}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-medium">
                        <span className="text-[#1E3A5F] font-bold">{lot.code}</span>
                        <span className="ml-2 text-[#2C2C2C]">{lot.nom}</span>
                        {lot.corps_etat && <span className="ml-2 text-[10px] text-[#9A9A9A]">{lot.corps_etat}</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {lot.offres_masquees ? (
                          // RP41 / D28 — AMO : aucune offre avant la date limite de remise
                          <span className="text-[10px] text-[#6B6B6B]" data-testid={`offres-masquees-${lot.id}`}
                            title="Les offres des entreprises seront visibles après la date limite de remise des offres.">
                            Après la date limite
                          </span>
                        ) : lot.nb_reponses > 0 ? (
                          <span className="text-xs px-2 py-0.5 bg-[#E8F5E9] text-[#2D6A4F] font-bold">
                            {lot.nb_reponses}
                          </span>
                        ) : (
                          <span className="text-xs text-[#9A9A9A]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {lot.prix_min !== null && lot.prix_max !== null ? (
                          lot.prix_min === lot.prix_max
                            ? fmtEur(lot.prix_min)
                            : `${fmtEur(lot.prix_min)} – ${fmtEur(lot.prix_max)}`
                        ) : <span className="text-[#9A9A9A]">—</span>}
                      </td>
                      <td className="px-3 py-2 text-[#2C2C2C]">
                        {lot.entreprise_attribuee || <span className="text-[#9A9A9A]">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-[#2D6A4F]">
                        {fmtEur(lot.montant_marche)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[10px] px-2 py-0.5 font-bold uppercase tracking-wider ${cfg.cls}`}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {lot.nb_reponses > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-none text-xs gap-1"
                            onClick={() => setExpandedId(isOpen ? null : lot.id)}
                            data-testid={`hub-voir-offres-${lot.id}`}
                          >
                            <Eye size={12} /> Voir les offres
                          </Button>
                        )}
                      </td>
                    </tr>

                    {isOpen && lot.propositions.length > 0 && (
                      <tr>
                        <td colSpan={8} className="bg-[#FAFAF8] p-0">
                          <div className="p-3" data-testid={`hub-lot-detail-${lot.id}`}>
                            <p className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-2 font-bold flex items-center gap-1">
                              <Trophy size={11} /> Propositions reçues — {lot.nom}
                            </p>
                            <div className="border border-[#E0E0DB] bg-white overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-[#F5F5F3] text-[#6B6B6B] text-[10px] uppercase tracking-wider">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Entreprise</th>
                                    <th className="px-3 py-2 text-right">Montant HT</th>
                                    <th className="px-3 py-2 text-center">TVA</th>
                                    <th className="px-3 py-2 text-right">Montant TTC</th>
                                    <th className="px-3 py-2 text-center">Mémoire / PJ</th>
                                    <th className="px-3 py-2 text-center">Date</th>
                                    <th className="px-3 py-2 text-right w-44">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...lot.propositions]
                                    .sort((a, b) => (a.montant_ht || 0) - (b.montant_ht || 0))
                                    .map((prop, idx) => (
                                      <tr
                                        key={prop.entreprise_id}
                                        className={`border-t border-[#E0E0DB] ${prop.est_retenue ? 'bg-[#E8F5E9]' : ''}`}
                                        data-testid={`hub-prop-${lot.id}-${prop.entreprise_id}`}
                                      >
                                        <td className="px-3 py-2 font-semibold">
                                          {idx === 0 && !prop.est_retenue && (
                                            <span className="text-[10px] mr-1 text-[#D97706]" title="Moins-disant">★</span>
                                          )}
                                          {prop.entreprise_nom}
                                          {prop.est_retenue && (
                                            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-[#2D6A4F] text-white uppercase font-bold tracking-wider">
                                              Retenue
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-right font-bold text-[#1E3A5F]">{fmtEur(prop.montant_ht)}</td>
                                        <td className="px-3 py-2 text-center text-[#6B6B6B]">{prop.tva_pct}%</td>
                                        <td className="px-3 py-2 text-right">{fmtEur(prop.montant_ttc)}</td>
                                        <td className="px-3 py-2 text-center text-[#6B6B6B]">
                                          <span className="inline-flex gap-1">
                                            {prop.has_memoire_technique
                                              ? <span title="Mémoire technique fournie"><FileText size={11} className="text-[#2D6A4F]" /></span>
                                              : <span title="Pas de mémoire" className="opacity-30"><FileText size={11} /></span>}
                                            {prop.has_pieces_jointes
                                              ? <span title="Pièces jointes"><Paperclip size={11} className="text-[#1E3A5F]" /></span>
                                              : <span title="Aucune PJ" className="opacity-30"><Paperclip size={11} /></span>}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-center text-[10px] text-[#6B6B6B]">{fmtDate(prop.date_soumission)}</td>
                                        <td className="px-3 py-2 text-right">
                                          <div className="flex items-center justify-end gap-1">
                                            <Button
                                              size="sm" variant="outline"
                                              onClick={() => setDetailEnt({ id: prop.entreprise_id, nom: prop.entreprise_nom })}
                                              className="rounded-none text-[10px] gap-1"
                                              data-testid={`hub-detail-${lot.id}-${prop.entreprise_id}`}
                                            >
                                              <Eye size={10} /> Détail
                                            </Button>
                                            {canWrite && lot.statut !== 'attribue' && !lot.dce_id && (
                                              <Button
                                                size="sm"
                                                onClick={() => handleAttribuer(lot.id, prop)}
                                                className="rounded-none text-[10px] gap-1 bg-[#C75B39] hover:bg-[#D97B5C] text-white"
                                                data-testid={`hub-attribuer-${lot.id}-${prop.entreprise_id}`}
                                                title="Attribution directe (lot manuel hors DCE)"
                                              >
                                                <Trophy size={10} /> Attribuer
                                              </Button>
                                            )}
                                            {lot.dce_id && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setComparatifCible({ dceId: lot.dce_id, lotId: lot.dpgf_lot_id })}
                                                className="rounded-none text-[10px] gap-1 border-[#0369A1] text-[#0369A1] hover:bg-[#E0F2FE]"
                                                data-testid={`hub-voir-comparatif-${lot.id}`}
                                                title={lot.statut === 'attribue' ? 'Consulter le comparatif DCE de ce lot' : "L'attribution finale passe par le comparatif DCE"}
                                              >
                                                <Trophy size={10} /> Voir comparatif DCE
                                              </Button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Statut connu seulement pour le DCE courant ; un ancien DCE s'ouvre en lecture */}
      <DCEComparatifDialog
        chantierId={chantierId}
        dceId={comparatifCible?.dceId}
        dceStatut={comparatifCible?.dceId === dceId ? dceStatut : 'ATTRIBUEE'}
        focusLotId={comparatifCible?.lotId}
        open={!!comparatifCible}
        onOpenChange={(o) => { if (!o) setComparatifCible(null); }}
        onAttribue={() => { reload(); onChanged?.(); onAttribuer?.(); }}
      />

      {detailEnt && dceId && (
        <EntrepriseReponseDetailModal
          chantierId={chantierId}
          dceId={dceId}
          entrepriseId={detailEnt.id}
          entrepriseNom={detailEnt.nom}
          onClose={() => setDetailEnt(null)}
        />
      )}

      {!dceId && detailEnt && (
        // Fallback : pas de DCE → on ferme
        // (cas rare où on a des marchés mais pas de consultation)
        (() => {
          toast.error('Aucune consultation associée à ce chantier.');
          setDetailEnt(null);
          return null;
        })()
      )}
    </div>
  );
}
